import { Eye } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SignaturePad, type SignaturePadHandle } from "@/components/common/signature-pad";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { createFirmSignature } from "@/features/settings/firm-api";
import { FIELD_LABEL } from "@/features/settings/components/settings-section";
import { Segmented } from "@/features/settings/components/segmented";
import { ApiError } from "@/lib/api";
import { HubStepShell } from "../hub/hub-step-shell";
import type { OnboardingStepProps } from "./types";

type CaptureMode = "draw" | "type" | "upload";
const MODES: { value: CaptureMode; label: string }[] = [
  { value: "draw", label: "Draw" },
  { value: "type", label: "Type" },
  { value: "upload", label: "Upload" },
];

/** Rasterise a typed name into a transparent PNG data URL in a script face. */
function typedSignatureDataUrl(text: string): string {
  const canvas = document.createElement("canvas");
  canvas.width = 640;
  canvas.height = 200;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  ctx.fillStyle = "#0D1B3E";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = '72px "Caveat", "Snell Roundhand", "Segoe Script", cursive';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  return canvas.toDataURL("image/png");
}

async function dataUrlToFile(dataUrl: string, name: string): Promise<File> {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], name, { type: "image/png" });
}

/** Step 3 — capture a firm signature (draw / type / upload) with a live
 * letterhead preview. Saves via `createFirmSignature`; the "use as e-signature"
 * checkbox maps to the signature's `isDefault` (the default is applied to forms). */
export function SignatureLetterheadStep({
  meta,
  index,
  isFirst,
  onBack,
  onSkip,
  onSaved,
}: OnboardingStepProps) {
  const firmName = useAuthStore((s) => s.user?.firm?.name) ?? "";
  const [mode, setMode] = useState<CaptureMode>("draw");
  const [printedName, setPrintedName] = useState(firmName);
  const [titleLine, setTitleLine] = useState("");
  const [typed, setTyped] = useState("");
  const [uploadUrl, setUploadUrl] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [useAsESignature, setUseAsESignature] = useState(true);
  const [drawVersion, setDrawVersion] = useState(0); // bump to re-read the pad
  const [submitting, setSubmitting] = useState(false);
  const padRef = useRef<SignaturePadHandle>(null);
  const uploadRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!printedName && firmName) setPrintedName(firmName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmName]);

  useEffect(() => () => {
    if (uploadUrl) URL.revokeObjectURL(uploadUrl);
  }, [uploadUrl]);

  // The data URL shown in the letterhead preview, by mode. Memoised so we don't
  // rasterise a fresh canvas on every unrelated re-render; `drawVersion` bumps
  // (via the pad's onChange) force a re-read while drawing.
  const previewSig = useMemo(() => {
    if (mode === "type") return typed.trim() ? typedSignatureDataUrl(typed.trim()) : null;
    if (mode === "upload") return uploadUrl;
    return padRef.current && !padRef.current.isEmpty() ? padRef.current.toDataURL() : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- drawVersion gates the pad re-read
  }, [mode, typed, uploadUrl, drawVersion]);

  function pickUpload(f: File | null) {
    setUploadFile(f);
    setUploadUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return f ? URL.createObjectURL(f) : null;
    });
  }

  /** The captured image as a File, or null when nothing was drawn/typed/picked. */
  async function captureFile(): Promise<File | null> {
    const label = (printedName.trim() || firmName || "Signature").slice(0, 80);
    if (mode === "upload") return uploadFile;
    if (mode === "type") {
      const t = typed.trim();
      return t ? dataUrlToFile(typedSignatureDataUrl(t), `${label}.png`) : null;
    }
    if (padRef.current && !padRef.current.isEmpty()) {
      return dataUrlToFile(padRef.current.toDataURL(), `${label}.png`);
    }
    return null;
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const file = await captureFile();
      if (file) {
        await createFirmSignature({
          name: printedName.trim() || firmName || "Signature",
          file,
          scale: 100,
          isDefault: useAsESignature,
        });
      }
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save your signature.");
      setSubmitting(false);
    }
  }

  const preview = (
    <>
      <span className="text-muted-foreground inline-flex items-center gap-2 text-xs font-semibold tracking-[0.08em] uppercase">
        <Eye className="size-3.5" /> Your first letter, already yours
      </span>
      <LetterheadPreview
        firmName={firmName || "Your firm"}
        printedName={printedName.trim() || firmName || "Your firm"}
        titleLine={titleLine.trim()}
        signatureUrl={previewSig}
      />
      <span className="text-muted-foreground text-xs">
        Updates as you sign. Address and logo come from steps 1 and 2.
      </span>
    </>
  );

  return (
    <form onSubmit={save}>
      <HubStepShell
        meta={meta}
        index={index}
        onBack={onBack}
        onSkip={onSkip}
        isFirst={isFirst}
        submitting={submitting}
        preview={preview}
      >
        <Segmented
          label="Signature method"
          value={mode}
          onChange={setMode}
          options={MODES}
        />

        {mode === "draw" && (
          <SignaturePad ref={padRef} onChange={() => setDrawVersion((v) => v + 1)} />
        )}
        {mode === "type" && (
          <div className="flex flex-col gap-2">
            <Input
              aria-label="Type your signature"
              placeholder="Type your name"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
            <div
              className="bg-background text-foreground flex h-28 items-center justify-center rounded-lg border text-4xl"
              style={{ fontFamily: '"Caveat", "Snell Roundhand", "Segoe Script", cursive' }}
            >
              {typed.trim() || <span className="text-muted-foreground text-base">Preview</span>}
            </div>
          </div>
        )}
        {mode === "upload" && (
          <div className="flex flex-col gap-2">
            <input
              ref={uploadRef}
              type="file"
              aria-label="Upload signature image"
              accept="image/png,image/jpeg,image/svg+xml"
              onChange={(e) => pickUpload(e.target.files?.[0] ?? null)}
            />
            {uploadUrl && (
              <div className="bg-background flex h-28 items-center justify-center rounded-lg border p-2">
                <img src={uploadUrl} alt="Signature" className="max-h-full object-contain" />
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="sig-name" className={FIELD_LABEL}>
            Printed name under signature
          </Label>
          <Input
            id="sig-name"
            value={printedName}
            onChange={(e) => setPrintedName(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="sig-title" className={FIELD_LABEL}>
            Title line
          </Label>
          <Input
            id="sig-title"
            placeholder="e.g. Enrolled Agent · Managing Partner"
            value={titleLine}
            onChange={(e) => setTitleLine(e.target.value)}
          />
        </div>

        <label className="flex cursor-pointer items-center gap-2.5 text-sm">
          <Checkbox
            checked={useAsESignature}
            onCheckedChange={(v) => setUseAsESignature(v === true)}
          />
          Also use this as my e-signature for client forms
        </label>
      </HubStepShell>
    </form>
  );
}

/** A miniature engagement-letter preview mirroring the mockup. The accent rule
 * uses the success (forest-green) token; address/logo are placeholders fed by
 * steps 1 & 2. */
function LetterheadPreview({
  firmName,
  printedName,
  titleLine,
  signatureUrl,
}: {
  firmName: string;
  printedName: string;
  titleLine: string;
  signatureUrl: string | null;
}) {
  const bar = (w: string) => (
    <span className="bg-muted inline-block h-2 rounded-full" style={{ width: w }} />
  );
  return (
    <div className="bg-card shadow-card relative flex flex-col gap-3.5 overflow-hidden rounded-md border p-6 text-[11px]">
      <span aria-hidden className="bg-success absolute inset-x-0 top-0 h-[5px]" />
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="bg-success-soft text-success flex h-10 w-16 items-center justify-center rounded-md border border-dashed text-[9px] font-bold tracking-wide uppercase">
            Logo
          </span>
          <span className="font-heading text-success text-sm font-bold">{firmName}</span>
        </div>
        <span className="text-muted-foreground text-right leading-relaxed">
          [Street address]
          <br />
          [City, State ZIP]
        </span>
      </div>
      <span className="bg-border h-px" />
      <div className="text-muted-foreground flex flex-col gap-2 leading-relaxed">
        <span className="text-foreground">Dear [Client name],</span>
        <span className="flex flex-col gap-1.5">
          <span>Thank you for choosing us to represent you before the IRS.</span>
          {bar("92%")}
          {bar("60%")}
        </span>
      </div>
      <div className="mt-1 flex flex-col gap-0.5">
        {signatureUrl ? (
          <img src={signatureUrl} alt="Signature" className="h-14 w-auto self-start object-contain" />
        ) : (
          <span className="border-muted-foreground/40 text-muted-foreground mb-1 w-28 border-b pb-6 text-[10px] italic">
            [Signature]
          </span>
        )}
        <span className="font-semibold">{printedName}</span>
        <span className="text-muted-foreground">{titleLine || "[Title line]"}</span>
      </div>
    </div>
  );
}
